// What a theme must read at: text on the page and its surfaces, text on every fill, and lines that must be seen, derived
// tokens included. The generator keeps to them (its tests hold every set it makes to them), and the Themes page shows
// where a set falls short; Lureo Light and Dark are Lureo's brand as it is, and not held to them.
import { contrast, readColour, themeValues, tokenColour, type Rgb } from './colour';
import { TOKEN_GROUPS } from './tokens';

// What a theme's own text gives on its page is the bar for everything derived from it: body text (4.5) where the theme
// reaches it, else 90% of its own. Bold text, icons and lines get 3 where the theme reaches it; pairs a theme's makers
// chose as a set (its text on its page, its selection pair, its red and green) are held only to a floor.
export type Bar = 'text' | 'bold' | 'theirs';

/** The palette's bright fills: the hues with an ink for text on the page. */
const PALETTE = TOKEN_GROUPS.find(group => group.id === 'palette')!.tokens.map(token => token.name);
export const FILL_SLOTS = PALETTE.filter(name => PALETTE.includes(`${name}-ink`));

/** The hover and pressed fills, each laid over the button it washes (both ends of the soft button's gradient); any other see-through fill sits on the surface. */
const LAID_OVER: Readonly<Record<string, readonly string[]>> = {
  'lureo-cta-hover': ['lureo-cta'],
  'lureo-cta-pressed': ['lureo-cta'],
  'lureo-cta-soft-hover': ['lureo-cta-soft-from', 'lureo-cta-soft-to'],
  'lureo-cta-soft-pressed': ['lureo-cta-soft-from', 'lureo-cta-soft-to'],
  'lureo-secondary-hover': ['lureo-secondary'],
  'lureo-secondary-pressed': ['lureo-secondary'],
  'lureo-menu-hover': ['lureo-menu'],
};

/** Every pair that must read: [text, what it sits on, the bar]. */
export const CONTRAST_PAIRS: readonly [string, string, Bar][] = [
  ['lureo-page-ink-strong', 'lureo-page', 'theirs'],
  ['lureo-ink-strong', 'lureo-surface', 'theirs'],
  ['lureo-on-emphasis-subtlest', 'lureo-secondary', 'theirs'],
  ['lureo-on-brand', 'lureo-brand', 'theirs'],
  ['lureo-on-cta', 'lureo-cta', 'theirs'],
  ['lureo-on-cta-soft', 'lureo-cta-soft-from', 'theirs'],
  ['lureo-on-cta-soft', 'lureo-cta-soft-to', 'theirs'],
  // The soft and solid buttons' labels on their hover and pressed fills, laid over the button
  ['lureo-on-cta-hover', 'lureo-cta-soft-hover', 'theirs'],
  ['lureo-on-cta-pressed', 'lureo-cta-soft-pressed', 'theirs'],
  ['lureo-on-secondary-hover', 'lureo-secondary-hover', 'theirs'],
  ['lureo-on-secondary-pressed', 'lureo-secondary-pressed', 'theirs'],
  // The call to action's label on its hover and pressed washes (its own label colours), an outline button's and an unchosen chip's under the pointer
  ['lureo-cta-label-hover', 'lureo-cta-hover', 'theirs'],
  ['lureo-cta-label-pressed', 'lureo-cta-pressed', 'theirs'],
  ['lureo-on-outline-hover', 'lureo-outline-hover', 'theirs'],
  ['lureo-on-outline-hover', 'lureo-outline-pressed', 'theirs'],
  ['lureo-on-chip-hover', 'lureo-chip-hover', 'text'],
  // The timeline ruler's time labels on the timeline
  ['lureo-timeline-ruler-ink', 'lureo-backdrop-from', 'text'],
  ['lureo-on-tooltip', 'lureo-tooltip', 'text'],
  ['lureo-on-segment-on', 'lureo-segment-on', 'text'],
  ['lureo-on-timeline-clip-on', 'lureo-timeline-clip-on', 'theirs'],
  // Text on cards under the pointer, fields, pop-ups, list menus and their rows under the pointer, and dialogs
  ['lureo-ink-strong', 'lureo-card-hover', 'text'],
  ['lureo-ink', 'lureo-field', 'text'],
  ['lureo-ink-strong', 'lureo-popover', 'text'],
  ['lureo-on-menu', 'lureo-menu', 'text'],
  ['lureo-on-menu-hover', 'lureo-menu-hover', 'text'],
  ['lureo-on-menu-subtle', 'lureo-menu', 'bold'],
  ['lureo-ink', 'lureo-dialog', 'text'],
  ['lureo-danger-ink', 'lureo-page', 'theirs'],
  ['lureo-success-ink', 'lureo-page', 'theirs'],
  ['lureo-on-emphasis', 'lureo-emphasis-strong', 'theirs'],
  ['lureo-on-emphasis', 'lureo-violet', 'theirs'],
  ['lureo-page-ink', 'lureo-page', 'text'],
  ['lureo-page-link', 'lureo-page', 'text'],
  ['lureo-link', 'lureo-surface', 'bold'],
  ['lureo-link-visited', 'lureo-surface', 'bold'],
  ['lureo-link-pressed', 'lureo-surface', 'bold'],
  ['lureo-ink-strong', 'lureo-neutral', 'text'],
  ['lureo-ink-strong', 'lureo-neutral-strong', 'text'],
  ['lureo-warning-ink', 'lureo-page', 'bold'],
  ['lureo-on-emphasis', 'lureo-danger', 'bold'],
  ['lureo-on-emphasis', 'lureo-emphasis', 'text'],
  ['lureo-on-emphasis-subtle', 'lureo-emphasis-strong', 'text'],
  ['lureo-plan-link', 'lureo-emphasis-strong', 'bold'],
  ['lureo-map-label', 'lureo-map-land', 'theirs'],
  // Text on the controls' own fills, a wash laid over the surface where the fill is see-through
  ['lureo-on-secondary', 'lureo-secondary', 'text'],
  ['lureo-bar-ink', 'lureo-bar', 'text'],
  ['lureo-on-tab-on', 'lureo-tab-on', 'text'],
  ['lureo-on-chip', 'lureo-chip', 'text'],
  ['lureo-on-chip-on', 'lureo-chip-on', 'bold'],
  ['lureo-on-chip-on', 'lureo-chip-on-hover', 'bold'],
  ['lureo-on-selected', 'lureo-selected', 'bold'],
  ['lureo-overline-ink', 'lureo-surface', 'bold'],
  ['lureo-page-link-hover', 'lureo-page', 'bold'],
  ['lureo-link-hover', 'lureo-surface', 'bold'],
  ['lureo-focus', 'lureo-surface', 'bold'],
  // An ink on a bright fill as the brand pairs them, and each fill's ink as text on the page
  ...FILL_SLOTS.flatMap(fill => [
    ['lureo-emphasis-strong', fill, 'text'] as [string, string, Bar],
    [`${fill}-ink`, 'lureo-page', 'text'] as [string, string, Bar],
  ]),
];

/** A pair that falls short: its text, what it sits on, the contrast it has and the contrast it needs. */
export interface ContrastShortfall {
  text: string;
  fill: string;
  ratio: number;
  minimum: number;
}

/** The pairs a theme's tokens fall short on, in its mode; a token that is not a colour counts as falling short. */
export function contrastShortfalls(tokens: Record<string, string>, mode: 'light' | 'dark'): ContrastShortfall[] {
  const valueOf = themeValues(tokens, mode);
  const rgb = (name: string) => tokenColour(name, valueOf)?.rgb;
  // A colour as it shows over what is under it: a see-through wash laid over that
  const over = (name: string, under: Rgb | undefined): Rgb | undefined => {
    const colour = tokenColour(name, valueOf);
    if (!colour || colour.alpha >= 1 || !under) return colour?.rgb;
    return colour.rgb.map((channel, i) => channel * colour.alpha + under[i] * (1 - colour.alpha)) as Rgb;
  };
  // A set's own contrast caps what it is held to: the page ink on the page for pairs on the page, the panel ink on the
  // surface for the rest
  const ownOf = (ink: string, fill: string) => {
    const a = rgb(ink);
    const b = rgb(fill);
    return a && b ? contrast(a, b) : 1;
  };
  const onPage = ownOf('lureo-page-ink-strong', 'lureo-page');
  const onPanel = ownOf('lureo-ink-strong', 'lureo-surface');
  const barsFor = (own: number): Record<Bar, number> => ({ text: Math.min(4.5, 0.9 * own), bold: Math.min(3, 0.9 * own), theirs: 1.5 });
  const bars = { page: barsFor(onPage), panel: barsFor(onPanel) };
  return CONTRAST_PAIRS.flatMap(([text, fill, bar]) => {
    const minimum = bars[fill === 'lureo-page' ? 'page' : 'panel'][bar] - 0.005;
    // Held to the weakest of what the fill lies on
    const ratio = Math.min(...(LAID_OVER[fill] ?? [fill === 'lureo-page' ? 'lureo-page' : 'lureo-surface']).map(under => {
      // A see-through button is laid over the surface first, then the wash over it
      const b = over(fill, over(under, rgb('lureo-surface')));
      const a = over(text, b);
      return a && b ? contrast(a, b) : 0;
    }));
    return ratio >= minimum ? [] : [{ text, fill, ratio, minimum }];
  }).concat(ctaFillShortfalls(tokens, valueOf, over, rgb, bars.panel.theirs - 0.005));
}

// Every colour a gradient names: oklch(), rgb() or rgba(), or hex
const GRADIENT_COLOURS = /oklch\([^)]*\)|rgba?\([^)]*\)|#[0-9a-f]{3,8}\b/gi;

/**
 * The call to action's own fill (a radial or conic gradient drawn over its straight one): its label, at rest, under the pointer
 * and pressed, held at every colour the fill names, each laid over the call to action; none draws nothing to check.
 */
function ctaFillShortfalls(
  tokens: Record<string, string>,
  valueOf: (name: string) => string | undefined,
  over: (name: string, under: Rgb | undefined) => Rgb | undefined,
  rgb: (name: string) => Rgb | undefined,
  minimum: number,
): ContrastShortfall[] {
  const fill = (tokens['lureo-cta-fill'] ?? 'none').trim();
  if (fill === 'none') return [];
  const under = over('lureo-cta', rgb('lureo-surface'));
  const stops = (fill.match(GRADIENT_COLOURS) ?? []).map(readColour).flatMap(colour => {
    if (!colour) return [];
    if (colour.alpha >= 1 || !under) return [colour.rgb];
    return [colour.rgb.map((channel, i) => channel * colour.alpha + under[i] * (1 - colour.alpha)) as Rgb];
  });
  return ['lureo-on-cta', 'lureo-cta-label-hover', 'lureo-cta-label-pressed'].flatMap(text => {
    const label = tokenColour(text, valueOf);
    if (!label) return [];
    const ratio = Math.min(...stops.map(stop => contrast(label.rgb, stop)));
    return stops.length && ratio < minimum ? [{ text, fill: 'lureo-cta-fill', ratio, minimum }] : [];
  });
}
